import React, { useCallback, useState, useEffect } from 'react';
import { AdSlotProps, OutfitData } from '../../types/ads';
import { User } from '../../types';
import { UserPreferences } from '../../utils/preferencesUtils';
import { useAuth } from "../../contexts/AuthContext";
import { targetingService, TargetingContext } from '../../services/TargetingService';
import AdSlot from './AdSlot';

/**
 * Enhanced AdSlot component with contextual targeting capabilities
 * Automatically generates targeting data based on page context, outfit data, and user behavior
 */
interface ContextualAdSlotProps extends Omit<AdSlotProps, 'targeting'> {
  /** Outfit data for contextual targeting */
  outfitData?: OutfitData;
  /** User preferences for demographic targeting */
  userPreferences?: UserPreferences;
  /** Search query for search-based targeting */
  searchQuery?: string;
  /** Additional manual targeting data */
  additionalTargeting?: AdSlotProps['targeting'];
  /** Enable/disable contextual targeting */
  enableContextualTargeting?: boolean;
}

const ContextualAdSlot: React.FC<ContextualAdSlotProps> = ({
  slotId,
  sizes,
  outfitData,
  userPreferences,
  searchQuery,
  additionalTargeting = {},
  enableContextualTargeting = true,
  className = '',
  lazy = true,
  refreshInterval,
  onLoad,
  onError
}) => {
  const { user } = useAuth();
  const [targeting, setTargeting] = useState<any>(additionalTargeting);

  /**
   * Generate contextual targeting data based on current context
   */
  const generateContextualTargeting = useCallback(async () => {
    if (!enableContextualTargeting) {
      return additionalTargeting;
    }

    try {
      // Determine page type from current URL
      const pathname = window.location.pathname;
      let pageType: TargetingContext['pageType'] = 'home';
      
      if (pathname.includes('/outfit/')) {
        pageType = 'outfit-detail';
      } else if (pathname.includes('/search')) {
        pageType = 'search';
      } else if (pathname.includes('/profile')) {
        pageType = 'profile';
      } else if (pathname.includes('/upload')) {
        pageType = 'upload';
      }

      // Build targeting context
      const context: TargetingContext = {
        pageContext: pageType || 'unknown',
        pageType,
        outfitData,
        user: user || undefined,
        userPreferences,
        searchQuery
      };

      // Generate targeting data using the targeting service
      const contextualTargeting = await targetingService.generateTargeting(context);

      // Merge with additional targeting data
      const finalTargeting = {
        ...contextualTargeting,
        ...additionalTargeting
      };

      // Convert targeting data to the format expected by AdSlot
      const adSlotTargeting: Record<string, string | string[]> = {};

      // Add keywords
      if (finalTargeting.keywords && finalTargeting.keywords.length > 0) {
        adSlotTargeting.keywords = finalTargeting.keywords;
      }

      // Add categories
      if (finalTargeting.categories && finalTargeting.categories.length > 0) {
        adSlotTargeting.categories = finalTargeting.categories;
      }

      // Add targeting data from backend
      if (finalTargeting.demographics) {
        if (finalTargeting.demographics.ageRange) {
          adSlotTargeting.age_range = Array.isArray(finalTargeting.demographics.ageRange) 
            ? `${finalTargeting.demographics.ageRange[0]}-${finalTargeting.demographics.ageRange[1]}`
            : finalTargeting.demographics.ageRange;
        }
        if (finalTargeting.demographics.gender) {
          adSlotTargeting.gender = finalTargeting.demographics.gender;
        }
        if (finalTargeting.demographics.location) {
          adSlotTargeting.location = finalTargeting.demographics.location;
        }
        if (finalTargeting.demographics.interests && finalTargeting.demographics.interests.length > 0) {
          adSlotTargeting.interests = finalTargeting.demographics.interests;
        }
      }

      // Add behavioral data
      if (finalTargeting.behavioral) {
        if (finalTargeting.behavioral.engagementLevel) {
          adSlotTargeting.engagement_level = finalTargeting.behavioral.engagementLevel;
        }
      }

      // Add contextual data
      if (finalTargeting.contextual) {
        if ('pageType' in finalTargeting.contextual && finalTargeting.contextual.pageType) {
          adSlotTargeting.page_type = finalTargeting.contextual.pageType;
        } else if ('currentPage' in finalTargeting.contextual && finalTargeting.contextual.currentPage) {
          adSlotTargeting.page_type = finalTargeting.contextual.currentPage;
        }
        if ('fashionStyles' in finalTargeting.contextual && finalTargeting.contextual.fashionStyles && finalTargeting.contextual.fashionStyles.length > 0) {
          adSlotTargeting.fashion_styles = finalTargeting.contextual.fashionStyles;
        }
        if ('brands' in finalTargeting.contextual && finalTargeting.contextual.brands && finalTargeting.contextual.brands.length > 0) {
          adSlotTargeting.brands = finalTargeting.contextual.brands;
        }
        if ('events' in finalTargeting.contextual && finalTargeting.contextual.events && finalTargeting.contextual.events.length > 0) {
          adSlotTargeting.events = finalTargeting.contextual.events;
        }
        if ('seasonality' in finalTargeting.contextual && finalTargeting.contextual.seasonality) {
          adSlotTargeting.season = finalTargeting.contextual.seasonality;
        }
      }

      return adSlotTargeting;

    } catch (error) {
      console.error('Error generating contextual targeting:', error);
      return additionalTargeting;
    }
  }, [
    enableContextualTargeting,
    outfitData,
    user,
    userPreferences,
    searchQuery,
    additionalTargeting
  ]);

  /**
   * Handle ad load success with contextual tracking
   */
  const handleAdLoad = useCallback(() => {
    // Track successful ad load with context
    if (enableContextualTargeting && user) {
      try {
        // Update user targeting data for better future targeting
        targetingService.updateUserTargeting(user.id, {
          contextual: {
            currentPage: window.location.pathname,
            timeOfDay: new Date().getHours() < 12 ? 'morning' : 'afternoon',
            deviceType: window.innerWidth < 768 ? 'mobile' : 'desktop',
            seasonality: 'current'
          }
        });
      } catch (error) {
        console.error('Error updating user profile:', error);
      }
    }

    onLoad?.();
  }, [enableContextualTargeting, user, slotId, outfitData, searchQuery, onLoad]);

  /**
   * Handle ad errors with contextual information
   */
  const handleAdError = useCallback((error: any) => {
    // Add contextual information to error
    const contextualError = {
      ...error,
      context: {
        ...error.context,
        pageType: window.location.pathname,
        outfitId: outfitData?.id,
        userId: user?.id,
        searchQuery,
        hasContextualTargeting: enableContextualTargeting
      }
    };

    onError?.(contextualError);
  }, [outfitData, user, searchQuery, enableContextualTargeting, onError]);

  // Generate targeting data on mount and when key dependencies change
  useEffect(() => {
    let isMounted = true;
    
    const loadTargeting = async () => {
      try {
        const result = await generateContextualTargeting();
        if (isMounted) {
          setTargeting(result);
        }
      } catch (error) {
        console.error('Error generating contextual targeting:', error);
        if (isMounted) {
          setTargeting(additionalTargeting);
        }
      }
    };
    
    loadTargeting();
    
    return () => {
      isMounted = false;
    };
  }, [
    // Only re-run when these specific values change, not the function reference
    enableContextualTargeting,
    outfitData?.id,
    user?.id,
    searchQuery,
    JSON.stringify(additionalTargeting)
  ]);

  return (
    <AdSlot
      slotId={slotId}
      sizes={sizes}
      targeting={targeting}
      className={`contextual-ad-slot ${className}`}
      lazy={lazy}
      refreshInterval={refreshInterval}
      onLoad={handleAdLoad}
      onError={handleAdError}
    />
  );
};

export default ContextualAdSlot;